import { Check } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type OpcaoEscolha<T extends string> = {
  valor: T;
  rotulo: string;
  /** Segunda linha, quando o rótulo sozinho não explica. Opcional e curta. */
  detalhe?: string;
  icone?: LucideIcon;
};

/**
 * Escolha entre poucas opções — tema, idioma, unidade de medida.
 *
 * Empilhada, nunca lado a lado. Três pílulas lado a lado numa tela de 390px
 * dão alvos de 110×40px com texto de 13px, e é assim que o app estava. Em
 * linha cheia cada opção tem a largura inteira, 72px de altura e o rótulo no
 * tamanho de leitura.
 *
 * O selecionado é marcado por um tique de 24px e pelo peso do texto, não só
 * por inversão de cor: quem enxerga mal distingue forma melhor que fundo.
 */
export function Escolha<T extends string>({
  opcoes,
  valor,
  onEscolher,
}: {
  opcoes: OpcaoEscolha<T>[];
  valor: T;
  onEscolher: (v: T) => void;
}) {
  return (
    <div style={{ paddingLeft: "var(--gutter)", paddingRight: "var(--gutter)" }}>
      <div className="overflow-hidden cartao">
        {opcoes.map((o, i) => {
          const ativo = o.valor === valor;
          return (
            <button
              key={o.valor}
              type="button"
              aria-pressed={ativo}
              onClick={() => onEscolher(o.valor)}
              className={`flex w-full items-center gap-4 px-5 text-left ${
                i > 0 ? "border-t border-border" : ""
              }`}
              style={{ minHeight: "var(--alvo-escolha)" }}
            >
              {o.icone && (
                <o.icone
                  className="h-6 w-6 shrink-0"
                  strokeWidth={2}
                  style={{ color: "var(--tinta-apoio)" }}
                />
              )}
              <span className="min-w-0 flex-1">
                <span
                  className={`block leading-tight ${ativo ? "font-bold" : "font-medium"}`}
                  style={{ fontSize: "var(--t-linha)" }}
                >
                  {o.rotulo}
                </span>
                {o.detalhe && (
                  <span
                    className="mt-0.5 block leading-snug"
                    style={{ fontSize: "var(--t-apoio)", color: "var(--tinta-apoio)" }}
                  >
                    {o.detalhe}
                  </span>
                )}
              </span>
              {ativo && <Check className="h-6 w-6 shrink-0" strokeWidth={3} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
